import * as React from 'react'
import { IconButton, Icon, Slider } from '@material-ui/core'
import ReactPlayer from 'react-player'
import styled from 'styled-components'
import { useTranslation } from 'i18n'

function useSoundPlayer({ player, uri }) {
  const [isUriUnavailable, setIsUriUnavailable] = React.useState(false)
  const [duration, setDuration] = React.useState(null)
  const [playing, setPlaying] = React.useState(false)
  const [progress, setProgress] = React.useState(0)
  const [isSeeking, setIsSeeking] = React.useState(false)
  const [valueWhileSeeking, setValueWhileSeeking] = React.useState(null)

  React.useEffect(() => {
    player.current?.getInternalPlayer()?.load()
    setIsUriUnavailable(false)
  }, [player, uri])

  const onDuration = React.useCallback(
    (duration: number) => {
      setDuration(duration)
    },
    [setDuration]
  )

  const onProgress = React.useCallback(
    ({ playedSeconds }) => {
      setProgress(playedSeconds)
    },
    [setProgress]
  )

  const onEnded = React.useCallback(() => {
    player.current?.seekTo(0, 'seconds')
    setProgress(0)
    setPlaying(false)
  }, [player.current?.seekTo, setProgress, setPlaying])

  const onTogglePlay = React.useCallback(() => {
    setPlaying(_playing => !_playing)
  }, [setPlaying])

  const onChange = React.useCallback(
    (event, value) => {
      setIsSeeking(true)
      setValueWhileSeeking(value)
    },
    [setValueWhileSeeking]
  )

  const onChangeCommitted = React.useCallback(
    (event, value) => {
      setProgress(value)
      setValueWhileSeeking(null)
      setIsSeeking(false)
      player.current?.seekTo(value, 'seconds')
    },
    [setProgress, setValueWhileSeeking, setIsSeeking, player.current?.seekTo]
  )

  const onClickRewind = React.useCallback(() => {
    const newTime = Math.max(player.current?.getCurrentTime() - 10, 0)
    setProgress(newTime)
    player.current?.seekTo(newTime, 'seconds')
  }, [player.current, setProgress])

  const onClickForward = React.useCallback(() => {
    const newTime = Math.min(player.current?.getCurrentTime() + 10, duration)
    setProgress(newTime)
    player.current?.seekTo(newTime, 'seconds')
  }, [player.current, duration, setProgress])

  const onError = React.useCallback(
    err => {
      const mediaError = err?.target?.error
      console.error(mediaError)
      setIsUriUnavailable(true)
    },
    [setIsUriUnavailable]
  )

  return {
    isUriUnavailable,
    duration,
    onDuration,
    playing,
    progress,
    isSeeking,
    valueWhileSeeking,
    onTogglePlay,
    onProgress,
    onEnded,
    onChange,
    onChangeCommitted,
    onClickRewind,
    onClickForward,
    onError,
  }
}

const secondToMmss = (second: number) =>
  [Math.floor(second / 60), Math.floor(second % 60)]
    .map(timeNum => String(timeNum).padStart(2, '0'))
    .join(':')

const Component = ({ uri }) => {
  const player = React.useRef(null)

  const {
    isUriUnavailable,
    duration,
    onDuration,
    playing,
    progress,
    isSeeking,
    valueWhileSeeking,
    onTogglePlay,
    onProgress,
    onEnded,
    onChange,
    onChangeCommitted,
    onClickRewind,
    onClickForward,
    onError,
  } = useSoundPlayer({ player, uri })

  const sliderValue = isSeeking ? valueWhileSeeking : progress

  const { t } = useTranslation('components')

  return (
    <Root isError={isUriUnavailable}>
      <ReactPlayer
        ref={player}
        style={{ display: 'none' }}
        url={uri}
        config={{ file: { forceAudio: true } }}
        progressInterval={100}
        playing={playing}
        onDuration={onDuration}
        onProgress={onProgress}
        onEnded={onEnded}
        onError={onError}
      />
      {isUriUnavailable && (
        <div>{t('ResourcePlayer.SoundPlayer.uriInaccesibleOrUnplayable')}</div>
      )}
      {player.current && !isUriUnavailable && (
        <>
          <SeekBarArea>
            <Slider
              min={0}
              max={duration}
              defaultValue={0}
              value={sliderValue}
              onChange={onChange}
              onChangeCommitted={onChangeCommitted}
            />
            <TimeTextArea>
              <TimeText>{secondToMmss(sliderValue)}</TimeText>
              <TimeText>
                {Number.isFinite(duration) &&
                  secondToMmss(Math.floor(duration) - Math.floor(sliderValue))}
              </TimeText>
            </TimeTextArea>
          </SeekBarArea>
          <IconButtonArea>
            <IconButton onClick={onClickRewind}>
              <PlayerIcon size={30}>fast_rewind</PlayerIcon>
            </IconButton>
            <IconButton onClick={onTogglePlay}>
              <PlayerIcon size={40}>
                {playing ? 'pause' : 'play_arrow'}
              </PlayerIcon>
            </IconButton>
            <IconButton onClick={onClickForward}>
              <PlayerIcon size={30}>fast_forward</PlayerIcon>
            </IconButton>
          </IconButtonArea>
        </>
      )}
    </Root>
  )
}

export default Component

type RootProps = {
  isError: boolean
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 30px 0;
  background-color: ${({ isError }: RootProps) => (isError ? '#E5E5E5' : null)};
`

const SeekBarArea = styled.div`
  margin: 16px 16px 0;

  & .MuiSlider-root {
    padding: 10px 0;
  }

  & .MuiSlider-active {
    box-shadow: none !important;
  }

  & .MuiSlider-thumb {
    transform: translateY(1px);
    background-color: #4f5b62;

    &:hover {
      box-shadow: none;
    }
  }

  & .MuiSlider-rail {
    height: 4px;
    border-radius: 2px;
    background-color: #a0a0a0;
  }

  & .MuiSlider-track {
    height: 4px;
    border-radius: 2px;
    background-color: #4f5b62;
  }
`

const TimeTextArea = styled.div`
  display: flex;
  justify-content: space-between;
`

const TimeText = styled.span`
  font-size: 12px;
  color: #797979;
`

const IconButtonArea = styled.div`
  margin: 18px 32px 0;
  display: flex;
  justify-content: space-between;

  & .MuiButtonBase-root {
    padding: 0;

    &:hover {
      background-color: transparent;
    }
  }

  & .MuiTouchRipple-root {
    display: none;
  }
`

const PlayerIcon = styled(Icon)`
  color: #000000;
  padding: 0;
  font-size: ${({ size }: { size?: number }) => `${size}px !important` ?? null};
`
